import { range } from 'es-toolkit';

import { getUserVisibleOrDefaultExceptionMessage, ReadFile } from '@paragon/client-tools';
import {
  AnalysisHistoryRecord,
  AnalysisSet,
  CanaryMetadata,
  ComponentDesign,
  isFeaturePatternTruss,
  PerpendicularLine,
  PointSlopeLine,
  PointToPointLine,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  getLayoutModelsWithPendingChanges,
  getParentGuids,
  layoutModelsCreated,
  modelPropertyChanged,
  saveAllProjectChanges,
} from '@paragon/layout-models';
import { closeAllSnackbars, enqueueError, enqueueInfo } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { SettingsPanels } from '../../components/interface/subpanels/settings/SettingsPanel';
import { ReferenceLineIntersectionAndKey } from '../../library/relativeGeometry/getReferenceLineEditableIntersectionPoints';
import { isConsideredDesignItem, SelectableItemEntry } from '../../library/selection/types';
import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { withoutCreatingUndoSnapshot } from '../middlewares/editorMiddleware';
import { getAnalysisSetGuidToComponentDesignGuid } from '../selectors/analysisSetsSelectors';
import { getBlockingOperation } from '../selectors/blockingOperationSelectors';
import { getInitiallySelectedComponentDesignGuidsFromContextForSelectedEntries } from '../selectors/downloadSelectors';
import { getActiveComponentDesign, getActiveComponentDesignGuid } from '../selectors/getActiveComponentDesignSelector';
import { getAnalysisHistoryForActiveComponentDesign } from '../selectors/getAnalysisHistoryForActiveComponentDesignSelector';
import { getLumbersPanelComponentDesignTargets } from '../selectors/getLumbersPanelComponentDesignTargets';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { getActiveJobLayoutModel, isParagonRepairJobOpen } from '../selectors/jobContextSelectors';
import { getExistingComponentDesignNames, getUniqueComponentName } from '../selectors/uniqueComponentNameSelectors';
import { setAnalysisHistoryRecord } from '../slices/analysisHistorySlice';
import { analysisSetsSetActiveGuid, toggleLoadingAnalysis } from '../slices/analysisSetsSlice';
import { applicationClearOrganizationAndCustomerGuid, setComponentOrder } from '../slices/applicationSlice';
import { onCompletingAsyncRequest, onInitiatingUpdateFeaturesRequest } from '../slices/asyncRequestsSlice';
import {
  setComponent,
  setUnsavedComponentDesignFromMostRecentAnalysisSet,
  setUnsavedComponentDesignFromServer as setUnsavedComponentDesignFromServerRawActionCreator,
} from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import {
  ReplaceMaterialsType,
  setReplaceMaterialsPanelClosed,
  setReplaceMaterialsPanelOpen,
  setSettingsPanelClosed,
  setSettingsPanelOpen,
} from '../slices/panelDataSlice';
import { clearSelection } from '../slices/selectionsSlice';
import { startSetReferenceLineIntersection } from './insertActions';
import {
  loadAnalysisSetByGuid,
  loadSettings,
  receiveAnalysisSet,
  receiveComponentDesign,
  unloadInsertComponentDesign,
} from './loadActions';
import { cancelActiveAnalysisResultRequests } from './loadAnalysisResults';
import { showNotOnMobileDialog, showProjectWideSettingsDialog } from './popupActions';
import { forceClearSelection, selectActiveLoadCaseOrDefault, selectItem } from './selectActions';
import { applyComponentSettingsFromAnalysisSet } from './settingsActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';
import { updateUrlFromState } from './urlActions';

export const setActiveAnalysisSet =
  (analysisSetGuid: string | undefined): AsyncThunkActionReturn<AnalysisSet | undefined> =>
  async (dispatch, getState) => {
    const state = getState();
    let analysisSet;
    if (analysisSetGuid) {
      analysisSet = state.analysisSets.sets.find((set) => set.guid === analysisSetGuid);
    }

    if (analysisSetGuid === state.analysisSets.activeAnalysisSetGuid) {
      return analysisSet;
    }

    cancelActiveAnalysisResultRequests();

    dispatch(closeAllSnackbars());

    if (analysisSetGuid) {
      const spinnerId = dispatch(addSpinnerMessage('Loading Analysis...'));
      dispatch(toggleLoadingAnalysis());
      // load analysis set if not found
      if (analysisSet === undefined) {
        await dispatch(
          loadAnalysisSetByGuid(analysisSetGuid, {
            setAnalysisSetAsActive: true,
            skipJackGirderConnections: true,

            forceUpdateWorkingChanges: false,
          }),
        );
        analysisSet = getState().analysisSets.sets.find((set) => set.guid === analysisSetGuid);
        if (analysisSet === undefined) throw new Error('Could not find analysis set');
      } else {
        await dispatch(
          receiveAnalysisSet(analysisSet, {
            replaceWorkingChanges: false,
            setAnalysisSetAsActive: true,
            skipJackGirderConnections: true,
          }),
        );
      }
      dispatch(toggleLoadingAnalysis());
      dispatch(removeSpinnerMessage(spinnerId));
    } else {
      dispatch(analysisSetsSetActiveGuid(undefined));
    }

    dispatch(selectActiveLoadCaseOrDefault());

    return analysisSet;
  };

export const revertToComponentDesignFromAnalysisSet =
  (analysisSet: AnalysisSet): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Reverting Component...'));

    try {
      await dispatch(applyComponentSettingsFromAnalysisSet(analysisSet));

      dispatch(setUnsavedComponentDesignFromServer(analysisSet.componentDesign));

      await dispatch(setActiveAnalysisSet(undefined));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const abortSelectFiles = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  const { insertingObjectInfo } = state.application;
  if (insertingObjectInfo?.type !== 'componentDesign' || !insertingObjectInfo?.loadedInfo?.isLoaded) {
    return;
  }

  const { guid } = insertingObjectInfo.loadedInfo;
  const componentDesign = state.componentDesigns.data[guid];
  if (!componentDesign) {
    return;
  }

  // The active selection must be cleared before `unloadInsertComponentDesign` to prevent crashes if part of the
  //   unloaded component is selected.
  dispatch(clearSelection());

  dispatch(unloadInsertComponentDesign(componentDesign));

  const componentDesigns = getState().componentDesigns.data;

  const lastComponentDesign =
    insertingObjectInfo.previouslySelectedComponentDesignGuid != null
      ? componentDesigns[insertingObjectInfo.previouslySelectedComponentDesignGuid]
      : undefined;
  if (lastComponentDesign) {
    dispatch(selectItem(lastComponentDesign, 'componentDesign', { force: true }));
  } else {
    dispatch(forceClearSelection());
  }
};

export const changeAnalysisHistoryRecordName =
  (analysisHistoryRecord: AnalysisHistoryRecord, name: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Saving analysis name...'));

    const newAnalysisHistoryRecord = { ...analysisHistoryRecord, name };
    dispatch(setAnalysisHistoryRecord(newAnalysisHistoryRecord));

    try {
      await designService.analysisHistory.patch(newAnalysisHistoryRecord);
    } catch (exception) {
      dispatch(setAnalysisHistoryRecord(analysisHistoryRecord));
      console.error(exception);
      dispatch(enqueueError(`Saving the analysis name ${name} failed.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const createProfileOfActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) throw new Error('Could not find component.');
  await dispatch(createProfile(activeComponentDesign));
};

const createProfile =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage(`Creating profile for ${componentDesign.component.name}...`));
    try {
      const updatedComponentDesign =
        await designService.componentDesigns.postGenerateProfileFromComponentDesign(componentDesign);
      dispatch(setUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError(`Unable to create profile for ${componentDesign.component.name}.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const setUnsavedComponentDesignFromServer =
  (componentDesign: ComponentDesign): ThunkActionVoid =>
  (dispatch) => {
    dispatch(
      setUnsavedComponentDesignFromServerRawActionCreator({
        componentDesign,
        componentDesignGuid: componentDesign.guid,
      }),
    );
  };

export const updateFeatures = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) {
    throw new Error('No component is active.');
  }
  const { component } = activeComponentDesign;
  if (component.componentType !== 'FloorTruss') {
    return;
  }
  const requestGuid = crypto.randomUUID();
  dispatch(
    onInitiatingUpdateFeaturesRequest({
      componentDesignGuid: activeComponentDesign.guid,
      requestGuid,
    }),
  );
  const isActiveRequest = () => {
    const activeRequestData = getState().asyncRequests[activeComponentDesign.guid];
    return activeRequestData && activeRequestData.requestGuid === requestGuid;
  };
  try {
    const defineFeaturesResponse = await designService.componentDesigns.postDefineFeatures(activeComponentDesign);
    if (isActiveRequest()) {
      const currentComponentDesign = getState().componentDesigns.data[activeComponentDesign.guid];
      if (!currentComponentDesign) {
        return;
      }
      if (!isFeaturePatternTruss(currentComponentDesign.component)) {
        throw new Error('The component definition type has changed.');
      }
      const { zones } = defineFeaturesResponse;
      const newComponent = {
        ...currentComponentDesign.component,
        zones,
      };

      dispatch(
        setComponent(
          withoutCreatingUndoSnapshot({
            component: newComponent,
            componentDesignGuid: currentComponentDesign.guid,
          }),
        ),
      );
    }
  } catch (exception) {
    console.error(exception);
  } finally {
    dispatch(onCompletingAsyncRequest({ componentDesignGuid: activeComponentDesign.guid, requestGuid }));
  }
};

export const addComponentDesignToProject = (componentDesignGuid: string, parentGuid: string): AsyncThunkActionVoid =>
  addComponentDesignsToProject([componentDesignGuid], parentGuid);

export const addComponentDesignsToProject =
  (
    componentDesignGuids: ReadonlyArray<string>,
    parentGuid: string,
    quantities?: { readonly [componentDesignGuid: string]: number | undefined },
  ): AsyncThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    if (state.application.openContext?.type !== 'project') {
      return Promise.resolve();
    }

    const { projectGuid } = state.application.openContext;
    const allLayoutModels = getLayoutModelsWithPendingChanges(state);
    const models = componentDesignGuids.map((componentDesignGuid) => allLayoutModels[componentDesignGuid]!);
    const insertions = models.flatMap((model) => {
      let quantity = quantities?.[model.guid] ?? 1;
      // It's unlikely a quantity of 0 is correct in this context, so we'll clamp this to have a minimum of 1.
      quantity = quantity < 1 ? 1 : quantity;
      return range(quantity).map(() => ({
        type: 'NormalComponent' as const,
        parentGuid,
        objectGuid: model.guid,
      }));
    });

    dispatch(layoutModelsCreated({ projectGuid, models, insertions }));
    return dispatch(saveAllProjectChanges());
  };

const SETTINGS_NOT_ON_MOBILE_MESSAGE = 'Editing settings is not yet available on mobile.';

// `mobile` is implemented as a parameter to ensure it is always checked before displaying settings panels
export const showSettingsPanel =
  (panel: SettingsPanels, mobile: boolean, selectedEntries: readonly SelectableItemEntry[]): ThunkActionVoid =>
  (dispatch, getState) => {
    if (mobile) {
      dispatch(showNotOnMobileDialog(SETTINGS_NOT_ON_MOBILE_MESSAGE));
      return;
    }

    const activeComponentDesignGuid = getActiveComponentDesignGuid(getState());

    const selectedEntry = getSingleSelectedItemEntryOrNull(getState());
    if (!selectedEntry?.item.guid) return;

    if (selectedEntry.type === 'componentDesign') {
      dispatch(setSettingsPanelOpen({ panel, settingsPanelActiveModelGuid: selectedEntry.item.guid }));
    } else if (isConsideredDesignItem(selectedEntry.type)) {
      if (activeComponentDesignGuid == null)
        throw new Error('Invalid state: no active component design when component design items are selected.');
      dispatch(setSettingsPanelOpen({ panel, settingsPanelActiveModelGuid: activeComponentDesignGuid }));
    } else {
      const context = getState().application.openContext;
      if (context?.type === 'project') {
        if (panel === 'loadSettings' || panel === 'analysisSettings') {
          const initiallySelectedComponentDesignGuids =
            getInitiallySelectedComponentDesignGuidsFromContextForSelectedEntries(
              selectedEntries,
              getAnalysisSetGuidToComponentDesignGuid(getState()),
              activeComponentDesignGuid,
              getState().layout.trussEnvelopes,
            );

          dispatch(showProjectWideSettingsDialog(panel, initiallySelectedComponentDesignGuids ?? []));
        } else {
          dispatch(
            setSettingsPanelOpen({
              settingsPanelActiveModelGuid: context.projectGuid,
              panel,
            }),
          );
        }
      }
    }
  };

export const closeSettingsPanel = () => setSettingsPanelClosed();

export const showReplaceMaterialsPanel =
  (targetGuid: string, type: ReplaceMaterialsType): ThunkActionVoid =>
  (dispatch, getState) => {
    const targetComponents = getLumbersPanelComponentDesignTargets(getState(), targetGuid);
    if (targetComponents.length === 0) {
      dispatch(
        enqueueInfo(
          'No component designs are selected.  Please select a component design or a container with component design children.',
        ),
      );
      return;
    }

    dispatch(
      setReplaceMaterialsPanelOpen({
        targetGuid,
        replace: type,
      }),
    );
  };

export const closeReplaceMaterialsPanel = () => setReplaceMaterialsPanelClosed();

export const duplicateComponentDesign =
  (
    componentDesignGuid: string,
    options: {
      componentName: string;
      duplicateSettings: boolean;
      duplicateJackConnections: boolean;
      parentGuid: string | null;
    },
  ): AsyncThunkActionReturn<ComponentDesign> =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    const { componentDesign: duplicatedComponentDesign, timestamps } =
      await designService.componentDesigns.postDuplicateComponentDesign(
        componentDesignGuid,
        options,
        openContext?.type === 'project' ? openContext.projectGuid : undefined,
      );

    await dispatch(
      receiveComponentDesign(duplicatedComponentDesign, {
        commitComponentDesign: true,
        replaceWorkingChanges: true,
        isPersisted: true,
        skipJackGirderConnections: false,
        // If we are not duplicating settings, they are added on project save later on, so load them below instead
        skipSettings: !options.duplicateSettings,
      }),
    );
    dispatch(setComponentDesignTimestamps({ timestamps }));

    if (options.parentGuid) {
      await dispatch(addComponentDesignToProject(duplicatedComponentDesign.guid, options.parentGuid));
    }

    if (!options.duplicateSettings) {
      await dispatch(loadSettings(duplicatedComponentDesign.guid));
    }

    return duplicatedComponentDesign;
  };

export const duplicateActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const blockingOperation = getBlockingOperation(getState());
  if (blockingOperation) {
    dispatch(enqueueError(`Cannot duplicate a truss while ${blockingOperation.userVisibleDescription}.`));
    return;
  }

  const spinnerId = dispatch(addSpinnerMessage('Duplicating Component...'));
  const activeComponentDesign = getActiveComponentDesign(getState())!;

  const { openContext } = getState().application;

  const parentGuids = getParentGuids(getState(), activeComponentDesign.guid);
  const parentGuid =
    parentGuids.length === 1 ? parentGuids[0] : openContext?.type === 'project' ? openContext.projectGuid : null;
  const componentName = getUniqueComponentName(
    `${activeComponentDesign.component.name}-copy`,
    getExistingComponentDesignNames(getState()),
  );

  let duplicatedComponentDesign: ComponentDesign;
  try {
    duplicatedComponentDesign = await dispatch(
      duplicateComponentDesign(activeComponentDesign.guid, {
        componentName,
        duplicateSettings: true,
        duplicateJackConnections: true,
        parentGuid,
      }),
    );
  } catch (error) {
    dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Unable to duplicate component.')));
    console.error(error);

    return;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }

  dispatch(addNewComponentDesignsToJobLayoutModel([duplicatedComponentDesign.guid]));
  dispatch(selectItem(duplicatedComponentDesign, 'componentDesign'));
  focusCameraOnSelectedObjects();
};

/**
 * Update the `JobLayoutModel` to know about new component designs. Does not do anything to persist this relationship.
 */
export const addNewComponentDesignsToJobLayoutModel =
  (componentDesignGuids: string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isParagonRepairJobOpen(state)) {
      const jobLayoutModel = getActiveJobLayoutModel(state);
      if (!jobLayoutModel) throw new Error('Invalid state.');
      dispatch(
        modelPropertyChanged({
          modelGuids: [jobLayoutModel.guid],
          propertyName: 'componentDesignGuids',
          newValue: jobLayoutModel.componentDesignGuids.concat(componentDesignGuids),
        }),
      );
    }
  };

export const setWorkingChanges = () => analysisSetsSetActiveGuid(undefined);

export const processCustomSortFile =
  (file: ReadFile): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Processing sorting file...'));

    let response;

    try {
      response = await designService.sortingFiles.postParseSortingFile(file);
    } catch {
      dispatch(enqueueError('Unable to parse sorting file.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    if (response) {
      dispatch(setComponentOrder(response));
    }
  };

export const resetWorkingChangesForActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const history = getAnalysisHistoryForActiveComponentDesign(state);

  if (!history || history.length === 0) {
    console.error('Attempted to reset working changes for a component with no history.');
    return;
  }

  // The history entries are already sorted by getHistoryForActiveComponentDesign
  const mostRecentAnalysis = history[0];

  await dispatch(setActiveAnalysisSet(mostRecentAnalysis.analysisSetGuid));

  const mostRecentAnalysisSet = getState().analysisSets.sets.find(
    (set) => set.guid === mostRecentAnalysis.analysisSetGuid,
  );
  if (!mostRecentAnalysisSet) {
    console.error('Could not find most recent analysis set.');
    return;
  }

  await dispatch(applyComponentSettingsFromAnalysisSet(mostRecentAnalysisSet));

  const lastAnalyzedTimestamp =
    state.componentDesignTimestamps[mostRecentAnalysisSet.componentDesign.guid]?.lastAnalyzed;
  if (!lastAnalyzedTimestamp) {
    console.error('Attempted to reset working changes for a component with no history.');
    return;
  }

  dispatch(
    setUnsavedComponentDesignFromMostRecentAnalysisSet({
      componentDesignGuid: mostRecentAnalysisSet.componentDesign.guid,
      componentDesign: mostRecentAnalysisSet.componentDesign,

      // Since working changes are being reset, the lastUpdatedTimestamp needs to be reset as well
      lastUpdatedTimestamp: lastAnalyzedTimestamp,
    }),
  );
};

export const selectEditableReferenceLineIntersection =
  (intersection: ReferenceLineIntersectionAndKey): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const selectedEntry = getSingleSelectedItemEntryOrNull(state);

    if (selectedEntry?.type !== 'referenceLine') {
      console.assert(false);
      return;
    }

    switch (selectedEntry.item.lineType) {
      case 'Perpendicular':
        dispatch(
          startSetReferenceLineIntersection(
            selectedEntry.item,
            intersection.intersectionKeyPair.key1 as keyof PerpendicularLine,
            intersection.intersectionKeyPair.key2 as keyof PerpendicularLine,
          ),
        );
        break;

      case 'PointToPoint':
        dispatch(
          startSetReferenceLineIntersection(
            selectedEntry.item,
            intersection.intersectionKeyPair.key1 as keyof PointToPointLine,
            intersection.intersectionKeyPair.key2 as keyof PointToPointLine,
          ),
        );
        break;

      case 'PointSlope':
        dispatch(
          startSetReferenceLineIntersection(
            selectedEntry.item,
            intersection.intersectionKeyPair.key1 as keyof PointSlopeLine,
            intersection.intersectionKeyPair.key2 as keyof PointSlopeLine,
          ),
        );
        break;
    }
  };

export const sendCanaryReport =
  (causeOfDeath: string, metadata: CanaryMetadata = {}): ThunkActionVoid =>
  (_, getState) => {
    designService.canary.sendReport(causeOfDeath, getActiveComponentDesignGuid(getState()), metadata);
  };

export const logOutLinkUser = (): ThunkActionVoid => (dispatch) => {
  dispatch(applicationClearOrganizationAndCustomerGuid());
  dispatch(updateUrlFromState());
  window.location.reload();
};
